import { useContext, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCircleCheck,
  IconCircleX,
  IconDots,
  IconEdit,
  IconLeaf,
  IconPlus,
  IconTrash,
  IconWorld,
  IconWallet,
  IconBuildingStore,
  IconTrendingUp,
  IconCash,
  IconCoins,
  IconCreditCard,
  IconPigMoney,
  IconBusinessplan,
  IconHome,
  IconSchool,
  IconBriefcase,
  IconCurrencyDollar,
  IconPower,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { AppContext } from "../../contexts/AppContext";
import {
  IAvailableLoanPlansProps,
  LoanChannel,
  LoanStatus,
  RepaymentFrequency,
  RepaymentModel,
} from "../../types/Loan";
import { useToastNotification } from "../../hooks/useToastNotification";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";

const channelOptions: { label: string; value: LoanChannel }[] = [
  { label: "App", value: "app" },
  { label: "Website", value: "website" },
  { label: "Both", value: "both" },
];

const statusOptions: { label: string; value: LoanStatus }[] = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const repaymentFrequencyOptions: { label: string; value: RepaymentFrequency }[] = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Bi-weekly", value: "biweekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Yearly", value: "yearly" },
];

const repaymentModelOptions: { label: string; value: RepaymentModel }[] = [
  { label: "Straight Line", value: "straight_line" },
  { label: "Reducing Balance", value: "reducing_balance" },
  { label: "Interest Only", value: "interest_only" },
  { label: "Bullet", value: "bullet" },
];

const paymentSequenceOptions = ["Fees", "Interest", "Penalty", "Principal", "Charges"];

const iconOptions = [
  { label: "Wallet", value: "IconWallet", icon: IconWallet },
  { label: "Building Store", value: "IconBuildingStore", icon: IconBuildingStore },
  { label: "Trending Up", value: "IconTrendingUp", icon: IconTrendingUp },
  { label: "Cash", value: "IconCash", icon: IconCash },
  { label: "Coins", value: "IconCoins", icon: IconCoins },
  { label: "Credit Card", value: "IconCreditCard", icon: IconCreditCard },
  { label: "Pig Money", value: "IconPigMoney", icon: IconPigMoney },
  { label: "Business Plan", value: "IconBusinessplan", icon: IconBusinessplan },
  { label: "Home", value: "IconHome", icon: IconHome },
  { label: "School", value: "IconSchool", icon: IconSchool },
  { label: "Briefcase", value: "IconBriefcase", icon: IconBriefcase },
  { label: "Currency Dollar", value: "IconCurrencyDollar", icon: IconCurrencyDollar },
];

const getIconComponent = (iconName: string) => {
  const iconOption = iconOptions.find((opt) => opt.value === iconName);
  return iconOption ? iconOption.icon : IconWallet;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(value);

const channelLabel = (channel: LoanChannel) => {
  switch (channel) {
    case "app":
      return "App";
    case "website":
      return "Website";
    case "both":
      return "App & Website";
    default:
      return channel;
  }
};

const statusColor = (status: LoanStatus) => (status === "active" ? "green" : "gray");

const statusLabel = (status: LoanStatus) => (status === "active" ? "Active" : "Inactive");

type FormValues = {
  name: string;
  description: string;
  interestRate: number;
  minimumAmount: number;
  maximumAmount: number;
  minimumPeriod: number;
  maximumPeriod: number;
  minimumInterestAmount: number;
  maximumActiveLoans: number;
  benefitsText: string;
  channel: LoanChannel;
  status: LoanStatus;
  allowMultiTenor: boolean;
  repaymentFrequency: RepaymentFrequency;
  repaymentModel: RepaymentModel;
  paymentApplicationSequence: string[];
  iconName: string;
};

export default function LoanProducts() {
  const { availableLoanPlans, loanProductStats, isLoading, reloadData } = useContext(AppContext);
  const { success, error } = useToastNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingLoan, setEditingLoan] = useState<IAvailableLoanPlansProps | null>(null);
  const [loanToDelete, setLoanToDelete] = useState<IAvailableLoanPlansProps | null>(null);

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      description: "",
      interestRate: 10,
      minimumAmount: 0,
      maximumAmount: 0,
      minimumPeriod: 1,
      maximumPeriod: 1,
      minimumInterestAmount: 0,
      maximumActiveLoans: 1,
      benefitsText: "",
      channel: "app",
      status: "active",
      allowMultiTenor: false,
      repaymentFrequency: "monthly",
      repaymentModel: "straight_line",
      paymentApplicationSequence: ["Fees", "Interest", "Principal"],
      iconName: "IconWallet",
    },
    validate: {
      name: (value) => (value.trim().length === 0 ? "Name is required" : null),
      interestRate: (value) => (value < 0 ? "Interest rate cannot be negative" : null),
      minimumAmount: (value) => (value < 0 ? "Minimum amount cannot be negative" : null),
      maximumAmount: (value, values) =>
        value <= 0
          ? "Maximum amount must be greater than zero"
          : value < values.minimumAmount
          ? "Maximum amount must be greater than or equal to minimum amount"
          : null,
      minimumPeriod: (value, values) =>
        value <= 0
          ? "Minimum tenor must be at least 1 month"
          : value > values.maximumPeriod
          ? "Minimum tenor cannot exceed maximum tenor"
          : null,
      maximumPeriod: (value, values) =>
        value < values.minimumPeriod ? "Maximum tenor must be greater than or equal to minimum tenor" : null,
      minimumInterestAmount: (value) => (value < 0 ? "Minimum interest amount cannot be negative" : null),
      maximumActiveLoans: (value) => (value < 1 ? "Maximum active loans must be at least 1" : null),
      paymentApplicationSequence: (value) =>
        value.length === 0 ? "Select at least one payment application sequence" : null,
    },
  });

  const resetForm = () => {
    form.setValues({
      name: "",
      description: "",
      interestRate: 10,
      minimumAmount: 0,
      maximumAmount: 0,
      minimumPeriod: 1,
      maximumPeriod: 1,
      minimumInterestAmount: 0,
      maximumActiveLoans: 1,
      benefitsText: "",
      channel: "app",
      status: "active",
      allowMultiTenor: false,
      repaymentFrequency: "monthly",
      repaymentModel: "straight_line",
      paymentApplicationSequence: ["Fees", "Interest", "Principal"],
      iconName: "IconWallet",
    });
    form.resetDirty();
  };

  const handleOpenCreate = () => {
    setEditingLoan(null);
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (loan: IAvailableLoanPlansProps) => {
    setEditingLoan(loan);
    form.setValues({
      name: loan.name,
      description: loan.description ?? "",
      interestRate: loan.interestRate,
      minimumAmount: loan.minimumAmount,
      maximumAmount: loan.maximumAmount,
      minimumPeriod: loan.minimumPeriod || 1,
      maximumPeriod: loan.maximumPeriod || Math.max(loan.minimumPeriod || 1, 1),
      minimumInterestAmount: loan.minimumInterestAmount ?? 0,
      maximumActiveLoans: loan.maximumActiveLoans || 1,
      benefitsText: loan.benefits?.join("\n") ?? "",
      channel: loan.channel,
      status: loan.status,
      allowMultiTenor: Boolean(loan.allowMultiTenor),
      repaymentFrequency: loan.repaymentFrequency || "monthly",
      repaymentModel: loan.repaymentModel || "straight_line",
      paymentApplicationSequence:
        loan.paymentApplicationSequence && loan.paymentApplicationSequence.length > 0
          ? loan.paymentApplicationSequence
          : ["Fees", "Interest", "Principal"],
      iconName: loan.iconName || "IconWallet",
    });
    form.resetDirty();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLoan(null);
    resetForm();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    if (values.paymentApplicationSequence.length === 0) {
      error("Select at least one payment application sequence");
      return;
    }

    setIsSubmitting(true);

    const benefits = values.benefitsText
      .split(/\r?\n/)
      .map((benefit) => benefit.trim())
      .filter(Boolean);

    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      interestRate: Number(values.interestRate),
      minimumAmount: Number(values.minimumAmount),
      maximumAmount: Number(values.maximumAmount),
      minimumPeriod: Number(values.minimumPeriod),
      maximumPeriod: Number(values.maximumPeriod),
      minimumInterestAmount: Number(values.minimumInterestAmount),
      maximumActiveLoans: Number(values.maximumActiveLoans),
      allowMultiTenor: values.allowMultiTenor,
      repaymentFrequency: values.repaymentFrequency,
      repaymentModel: values.repaymentModel,
      paymentApplicationSequence: values.paymentApplicationSequence,
      benefits,
      channel: values.channel,
      status: values.status,
      iconName: values.iconName,
    };

    try {
      const endpoint = editingLoan ? Endpoints.UpdateLoanProduct(editingLoan.id) : Endpoints.CreateLoanProduct();
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: payload,
      });

      const successStatus = editingLoan ? HttpCodes.Success : HttpCodes.Created;
      if (response.status === successStatus) {
        success(`Loan product ${editingLoan ? "updated" : "created"} successfully`);
        await reloadData();
        handleCloseModal();
      } else {
        error(response.message || `Failed to ${editingLoan ? "update" : "create"} loan product`);
      }
    } catch (err: any) {
      error(err.message || `Failed to ${editingLoan ? "update" : "create"} loan product`);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDelete = async () => {
    if (!loanToDelete) return;
    setIsDeleting(true);
    try {
      const endpoint = Endpoints.DeleteLoanProduct(loanToDelete.id);
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === HttpCodes.Success) {
        success(`Loan product "${loanToDelete.name}" deleted successfully`);
        await reloadData();
        setLoanToDelete(null);
      } else {
        error(response.message || "Failed to delete loan product");
      }
    } catch (err: any) {
      error(err.message || "Failed to delete loan product");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (loan: IAvailableLoanPlansProps) => {
    try {
      const endpoint = Endpoints.ToggleLoanProductStatus(loan.id);
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === HttpCodes.Success) {
        const newStatus = loan.status === "active" ? "inactive" : "active";
        success(`Loan product status changed to ${newStatus}`);
        await reloadData();
      } else {
        error(response.message || "Failed to toggle loan product status");
      }
    } catch (err: any) {
      error(err.message || "Failed to toggle loan product status");
    }
  };

  const sortedProducts = useMemo(
    () => [...availableLoanPlans].sort((a, b) => b.createdAt - a.createdAt),
    [availableLoanPlans],
  );

  const tenorRange = (loan: IAvailableLoanPlansProps) => {
    const min = loan.minimumPeriod || 1;
    const max = loan.maximumPeriod || min;
    return `${min} - ${max} months`;
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Group justify="space-between" align="flex-end" mb="md">
            <div>
              <Title order={1} size="3rem" fw={800} style={{ color: "#679369", marginBottom: "0.5rem" }}>
                Loan Products
              </Title>
              <Text size="lg" c="dimmed">
                Create and manage the loan products available to customers
              </Text>
            </div>
            <Button leftSection={<IconPlus size={18} />} onClick={handleOpenCreate} color="green" variant="filled">
              Create Loan Product
            </Button>
          </Group>

          <Grid gutter="md" mb="lg">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)", border: "1px solid #699269" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#699269" }}>
                    <IconLeaf size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#558B2F">
                      Total Products
                    </Text>
                    <Text size="xl" fw={700} c="#699269">
                      {loanProductStats?.total ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)", border: "1px solid #22C55E" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#22C55E" }}>
                    <IconCircleCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#166534">
                      Active Products
                    </Text>
                    <Text size="xl" fw={700} c="#166534">
                      {loanProductStats?.active ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)", border: "1px solid #9CA3AF" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#9CA3AF" }}>
                    <IconCircleX size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#4B5563">
                      Inactive Products
                    </Text>
                    <Text size="xl" fw={700} c="#4B5563">
                      {loanProductStats?.inactive ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>
        </motion.div>

        <Paper shadow="sm" radius="md" withBorder>
          <ScrollArea>
            {isLoading ? (
              <Stack align="center" gap="sm" py="xl">
                <Loader color="green" />
                <Text size="sm" c="dimmed">
                  Loading loan products...
                </Text>
              </Stack>
            ) : sortedProducts.length === 0 ? (
              <Stack align="center" gap="sm" py="xl">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconWorld size={28} />
                </ThemeIcon>
                <Text fw={600} size="lg">
                  No loan products yet
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={320}>
                  Start by creating a loan product to make it available to your customers.
                </Text>
                <Button leftSection={<IconPlus size={18} />} onClick={handleOpenCreate}>
                  Create Loan Product
                </Button>
              </Stack>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>Amount Range</Table.Th>
                    <Table.Th>Interest Rate</Table.Th>
                    <Table.Th>Tenor</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedProducts.map((loan) => (
                    <Table.Tr key={loan.id}>
                      <Table.Td>
                        <Group align="center" gap="sm">
                          <ThemeIcon
                            size={40}
                            radius="md"
                            variant="light"
                            color="green"
                          >
                            {(() => {
                              const IconComponent = getIconComponent(loan.iconName || "IconWallet");
                              return <IconComponent size={20} />;
                            })()}
                          </ThemeIcon>
                          <Text fw={600}>{loan.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {formatCurrency(loan.minimumAmount)} - {formatCurrency(loan.maximumAmount)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{loan.interestRate}%</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{tenorRange(loan)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColor(loan.status)} variant="filled" size="sm">
                          {statusLabel(loan.status)}
                        </Badge>
                      </Table.Td>
                      <Table.Td width={80}>
                        <Menu withinPortal position="bottom-end" shadow="sm">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={16} />} onClick={() => handleOpenEdit(loan)}>
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconPower size={16} />}
                              onClick={() => handleToggleStatus(loan)}
                            >
                              {loan.status === "active" ? "Deactivate" : "Activate"}
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={16} />}
                              onClick={() => setLoanToDelete(loan)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>
        </Paper>
      </Stack>

      <Modal
        opened={isModalOpen}
        onClose={handleCloseModal}
        title={editingLoan ? "Edit Loan Product" : "Create Loan Product"}
        size="xl"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            <Stack gap="sm">
              <Text fw={600} size="sm">
                Product Icon
              </Text>
              <Group align="flex-start" gap="md">
                <Select
                  placeholder="Select an icon"
                  data={iconOptions.map((opt) => ({ label: opt.label, value: opt.value }))}
                  value={form.values.iconName}
                  onChange={(value) => form.setFieldValue("iconName", value || "IconWallet")}
                  required
                  style={{ flex: 1 }}
                />
                {form.values.iconName && (
                  <ThemeIcon
                    size={64}
                    radius="md"
                    variant="light"
                    color="green"
                    style={{ flexShrink: 0 }}
                  >
                    {(() => {
                      const IconComponent = getIconComponent(form.values.iconName);
                      return <IconComponent size={32} />;
                    })()}
                  </ThemeIcon>
                )}
              </Group>
            </Stack>

            <Divider label="Loan product details" labelPosition="center" />

            <TextInput label="Product Name" placeholder="e.g. Premium Offline Loan" required {...form.getInputProps("name")} />
            <Textarea
              label="Product Description"
              placeholder="Provide a short description of this loan product"
              autosize
              minRows={2}
              {...form.getInputProps("description")}
            />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Minimum Amount"
                  min={0}
                  thousandSeparator="," 
                  prefix="₦"
                  value={form.values.minimumAmount}
                  onChange={(value) => form.setFieldValue("minimumAmount", Number(value) || 0)}
                  error={form.errors.minimumAmount}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Maximum Amount"
                  min={0}
                  thousandSeparator="," 
                  prefix="₦"
                  value={form.values.maximumAmount}
                  onChange={(value) => form.setFieldValue("maximumAmount", Number(value) || 0)}
                  error={form.errors.maximumAmount}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <NumberInput
                  label="Interest Rate (%)"
                  min={0}
                  step={0.5}
                  value={form.values.interestRate}
                  onChange={(value) => form.setFieldValue("interestRate", Number(value) || 0)}
                  error={form.errors.interestRate}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <NumberInput
                  label="Minimum Interest Amount"
                  min={0}
                  thousandSeparator="," 
                  prefix="₦"
                  value={form.values.minimumInterestAmount}
                  onChange={(value) => form.setFieldValue("minimumInterestAmount", Number(value) || 0)}
                  error={form.errors.minimumInterestAmount}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <NumberInput
                  label="Maximum Active Loans"
                  min={1}
                  value={form.values.maximumActiveLoans}
                  onChange={(value) => form.setFieldValue("maximumActiveLoans", Number(value) || 1)}
                  error={form.errors.maximumActiveLoans}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Minimum Tenor (Months)"
                  min={1}
                  value={form.values.minimumPeriod}
                  onChange={(value) => form.setFieldValue("minimumPeriod", Number(value) || 1)}
                  error={form.errors.minimumPeriod}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Maximum Tenor (Months)"
                  min={1}
                  value={form.values.maximumPeriod}
                  onChange={(value) => form.setFieldValue("maximumPeriod", Number(value) || 1)}
                  error={form.errors.maximumPeriod}
                />
              </Grid.Col>
            </Grid>

            <Textarea
              label="Benefits"
              description="Enter one benefit per line"
              minRows={3}
              autosize
              {...form.getInputProps("benefitsText")}
            />

            <Box>
              <Text fw={500} size="sm" mb={4}>
                Channel
              </Text>
              <Group>
                {channelOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    checked={form.values.channel === option.value}
                    onChange={() => form.setFieldValue("channel", option.value)}
                  />
                ))}
              </Group>
            </Box>

            <Select
              label="Status"
              data={statusOptions}
              value={form.values.status}
              onChange={(value) => form.setFieldValue("status", (value as LoanStatus) || "active")}
            />

            <Divider label="Repayment details" labelPosition="center" />

            <Switch
              label="Allow multi tenor"
              checked={form.values.allowMultiTenor}
              onChange={(event) => form.setFieldValue("allowMultiTenor", event.currentTarget.checked)}
            />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Repayment Frequency"
                  data={repaymentFrequencyOptions}
                  value={form.values.repaymentFrequency}
                  onChange={(value) =>
                    form.setFieldValue("repaymentFrequency", (value as RepaymentFrequency) || "monthly")
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Repayment Model"
                  data={repaymentModelOptions}
                  value={form.values.repaymentModel}
                  onChange={(value) =>
                    form.setFieldValue("repaymentModel", (value as RepaymentModel) || "straight_line")
                  }
                />
              </Grid.Col>
            </Grid>

            <MultiSelect
              label="Payment Application Sequence"
              data={paymentSequenceOptions}
              placeholder="Select order of payment application"
              value={form.values.paymentApplicationSequence}
              onChange={(value) => form.setFieldValue("paymentApplicationSequence", value)}
              withAsterisk
              error={form.errors.paymentApplicationSequence as string | null}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {editingLoan ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={Boolean(loanToDelete)}
        onClose={() => setLoanToDelete(null)}
        title="Delete Loan Product"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <strong>{loanToDelete?.name}</strong>? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setLoanToDelete(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={isDeleting} leftSection={<IconTrash size={16} />}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
